import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { appInsights } from './services/AppInsightsService';
import './App.css';
import { Box, CircularProgress, CssBaseline, Theme } from '@mui/material';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import {
  Route,
  Routes
} from "react-router-dom";
import MainAppBar from './components/common/MainAppBar';
import { AuthProvider } from './services/Auth/AuthContext';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './services/AppInsightsService';
import config from './config';
import Unauthorized from './components/common/Unauthorized';
import { AuthService } from './services/Auth/AuthService';
import { SnackbarProvider } from 'notistack';

const Events = React.lazy(() => import('./components/events/Events'));
const Circuits = React.lazy(() => import('./components/circuits/Circuits'));
const EventDetail = React.lazy(() => import('./components/events/EventDetail'));
const ParticipantDetail = React.lazy(() => import('./components/participants/ParticipantDetail'));
const CircuitDetail = React.lazy(() => import('./components/circuits/CircuitDetail'));
const CircuitEventsPage = React.lazy(() => import('./components/circuits/CircuitEventsPage'));
const CircuitBikesHallOfFamePage = React.lazy(() => import('./components/circuits/CircuitBikesHallOfFamePage'));
const CircuitCarsHallOfFamePage = React.lazy(() => import('./components/circuits/CircuitCarsHallOfFamePage'));
const OrganizerDetail = React.lazy(() => import('./components/organizers/OrganizerDetail'));
const EventSetup = React.lazy(() => import('./components/events/setup/EventSetup'));
const Organizers = React.lazy(() => import('./components/organizers/Organizers'));
const OrganizerEvents = React.lazy(() => import('./components/events/setup/OrganizerEvents'));
const UserHistory = React.lazy(() => import('./components/user/UserHistory'));
const TagManagement = React.lazy(() => import('./components/user/TagManagement'));
const RegisterTag = React.lazy(() => import('./components/user/RegisterTag'));
const CircuitsSetup = React.lazy(() => import('./components/circuits/setup/CircuitsSetup'));
const ReadersSetup = React.lazy(() => import('./components/admin/readers/ReadersSetup'));
const ParticipantDummy = React.lazy(() => import('./components/participants/ParticipantDummy'));
const AdminTagManagement = React.lazy(() => import('./components/admin/tags/AdminTagManagement'));
const ReadersMonitor = React.lazy(() => import('./components/admin/monitor/ReadersMonitor'));
const AdminUserManagement = React.lazy(() => import('./components/admin/users/AdminUserManagement'));
const PoisonQueues = React.lazy(() => import('./components/admin/poison/PoisonQueues'));
const DiscardedTagsManagement = React.lazy(() => import('./components/admin/discarded/DiscardedTagsManagement'));
const Profile = React.lazy(() => import('./components/user/Profile'));
const AccessCode = React.lazy(() => import('./components/common/AccessCode'));
const SigninCallback = React.lazy(() => import('./components/account/SigninCallback'));
const SignoutCallback = React.lazy(() => import('./components/account/SignoutCallback'));
const RiderReadsPage = React.lazy(() => import('./components/events/RiderReadsPage'));
const HelpPage = React.lazy(() => import('./components/help/HelpPage'));

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    }
  }),
);

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    appInsights.trackPageView({
      name: location.pathname,
      uri: window.location.origin + location.pathname + location.search + location.hash,
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function App() {
  const classes = useStyles();
  // const authService = useContext(AuthContext);
  const [loaded, setLoaded] = useState(false);
  const [authService, setAuthService] = useState<AuthService>();
  useEffect(() => {
    if (!loaded) {
      AuthService.theInstance().then(asvc => {
        setAuthService(asvc);
        setLoaded(true);
      });

    }
  },[loaded]);

  return (

    <AppInsightsContext.Provider value={reactPlugin}>
    <SnackbarProvider>
      <AuthProvider value={authService}>
        <div className={classes.root}>
          <CssBaseline />
          <PageViewTracker />
          <MainAppBar />
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
            <Routes>
              <Route path="/circuits" element={<Circuits />} />
              <Route path="/circuits/:circuitId" element={<CircuitDetail />} />
              <Route path="/circuits/:circuitId/events" element={<CircuitEventsPage />} />
              <Route path="/circuits/:circuitId/hall-of-fame/bikes" element={<CircuitBikesHallOfFamePage />} />
              <Route path="/circuits/:circuitId/hall-of-fame/cars" element={<CircuitCarsHallOfFamePage />} />
              <Route path="/event/setup/:eventId" element={<EventSetup />} />
              <Route path="/event/:eventId" element={<EventDetail />} />
              <Route path="/rider/:eventId/:tagId" element={<ParticipantDetail />} />
              <Route path="/rider/:eventId" element={<ParticipantDetail />} />
              <Route path="/dummy" element={<ParticipantDummy />} />
              <Route path="/events/organizer" element={<OrganizerEvents />} />
              <Route path="/events" element={<Events />} />
              <Route path="/organizers" element={<Organizers />} />
              <Route path="/organizers/:organizerId" element={<OrganizerDetail />} />
              <Route path="/signin-callback" element={<SigninCallback />} />
              <Route path="/signout-callback" element={<SignoutCallback />} />
              <Route path="/user/profile" element={<Profile />} />
              <Route path="/user/history" element={<UserHistory />} />
              <Route path="/user/tags" element={<TagManagement />} />
              <Route path="/user/registerTag" element={<RegisterTag />} />
              {/* admin routes render null while auth loads to avoid a flash of <Unauthorized /> */}
              <Route path="/admin/circuits" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <CircuitsSetup /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/readers" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <ReadersSetup /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/tags" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <AdminTagManagement /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/monitoring" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <ReadersMonitor /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/users" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <AdminUserManagement /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/poison-queues" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <PoisonQueues /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/discarded-tags" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <DiscardedTagsManagement /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/admin/rider/:raceId/:tagId/reads" element={!loaded ? null : (authService?.isInRole(config.managementGroups.administrator) ? <RiderReadsPage /> : <Unauthorized />)} errorElement={<Unauthorized />} />
              <Route path="/access-code" element={<AccessCode />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/" element={<Events />} />
            </Routes>
          </React.Suspense>

        </div>
      </AuthProvider>
    </SnackbarProvider>
    </AppInsightsContext.Provider>

  );
}



export default App;
